import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { cn } from "@/lib/utils";

export interface PlatformPillar {
  /** Pillar number (e.g., "01") */
  number: string;
  /** Short category label */
  label: string;
  /** Main headline */
  headline: string;
  /** Body text */
  body: string;
}

export interface PlatformTableProps {
  /** Array of platform pillars */
  pillars: PlatformPillar[];
  /** Whether to animate on scroll */
  animate?: boolean;
  /** Additional className */
  className?: string;
}

/**
 * component-table-platform
 * 
 * A three-column editorial table for displaying platform pillars/philosophy.
 * 
 * Tokens applied:
 * - Container: space-y-0 (pillars separated by border-t)
 * - Row: grid md:grid-cols-[200px_1fr_1.5fr] gap-8 md:gap-16
 * - Border: border-t border-border
 * - Row padding: py-12 md:py-16
 * - Number: text-sm font-medium text-muted-foreground
 * - Label: text-sm font-medium text-foreground
 * - Headline: text-2xl md:text-3xl font-semibold text-foreground
 * - Body: text-sm text-foreground leading-relaxed
 * - Animation: fade-in-up on scroll (optional)
 */
export function PlatformTable({ 
  pillars, 
  animate = true,
  className = "" 
}: PlatformTableProps) {
  const [hasMounted, setHasMounted] = useState(false);
  useEffect(() => setHasMounted(true), []);

  return (
    <div className={cn("space-y-0", className)}>
      {pillars.map((pillar, index) => {
        const content = (
          <div className="group border-t border-border py-12 md:py-16">
            <div className="grid md:grid-cols-[200px_1fr_1.5fr] gap-8 md:gap-16">
              {/* Number & Label Column */}
              <div className="flex md:flex-col gap-4 md:gap-2">
                {/* atom-text-pillar-number */}
                <span className="text-sm font-medium text-muted-foreground">
                  {pillar.number}
                </span>
                {/* atom-text-pillar-label */}
                <span className="text-sm font-medium text-foreground">
                  {pillar.label}
                </span>
              </div>

              {/* Headline Column */}
              <div>
                {/* atom-text-pillar-headline */}
                <h3 className="text-2xl md:text-3xl font-semibold text-foreground leading-tight">
                  {pillar.headline}
                </h3>
              </div>

              {/* Body Column */}
              <div>
                {/* atom-text-pillar-body */}
                <p className="text-sm text-foreground leading-relaxed">
                  {pillar.body}
                </p>
              </div>
            </div>
          </div>
        );

        if (animate) {
          return (
            <motion.div
              key={pillar.number}
              {...(hasMounted && { initial: { opacity: 0, y: 20 } })}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
            >
              {content}
            </motion.div>
          );
        }

        return <div key={pillar.number}>{content}</div>;
      })}
    </div>
  );
}

export default PlatformTable;
